import { PAGE_LABELS } from "@src/constants/page-labels";
import type { UserRole } from "@src/context/ApplicationContext";

/**
 * "patient" = third-person copy · "self" = first-person copy.
 * Resolved by: enabled flag → age → role.
 */
type LabelMode = "patient" | "self";

type PageLabelMap = typeof PAGE_LABELS;

/** Flattens every `{ patient, self }` leaf in a page to `string`. */
type ResolvedPage<P extends keyof PageLabelMap> = {
  [K in keyof PageLabelMap[P]]: PageLabelMap[P][K] extends Record<LabelMode, string>
    ? string
    : never;
};

/**
 * Resolves the label voice ("patient" | "self") by precedence:
 * 1. `enabled=false`          → always "patient" (flag off)
 * 2. non-patient role         → always "patient" (navigator/referrer are third-person)
 * 3. patient role + `age`     → age < 18 → "patient", age >= 18 → "self"
 * 4. patient role (no age)    → "self"
 *
 * Age only influences the voice for the `patient` (self-booking) role; it is
 * ignored for navigator/referrer flows so they never render first-person copy.
 */
export function resolveLabelMode(
  role: UserRole,
  options?: { age?: number; enabled?: boolean },
): LabelMode {
  if (options?.enabled === false) {
    return "patient"; // flag off — always third-person
  }
  if (role !== "patient") {
    return "patient"; // navigator/referrer — always third-person, age ignored
  }
  if (options?.age !== undefined) {
    return options.age < 18 ? "patient" : "self";
  }
  return "self";
}

/**
 * Resolves `page` labels to a flat `Record<key, string>` using `resolveLabelMode`
 * (see its precedence: enabled flag → non-patient role → patient age → patient default).
 */
export function resolvePageLabels<P extends keyof PageLabelMap>(
  page: P,
  role: UserRole,
  options?: { age?: number; enabled?: boolean },
): ResolvedPage<P> {
  const mode = resolveLabelMode(role, options);

  const pageLabels = PAGE_LABELS[page] as unknown as Record<string, Record<LabelMode, string>>;

  return Object.fromEntries(
    Object.entries(pageLabels).map(([key, variants]) => [key, variants[mode]]),
  ) as ResolvedPage<P>;
}

/** Picks the right screener question array by role/flag (`enabled=false` forces patientQuestions). */
export function resolveScreenerQuestions<T>(
  selfQuestions: T[],
  patientQuestions: T[],
  role: UserRole | undefined,
  options?: { enabled?: boolean; age?: number },
): T[] {
  const isSelfMode =
    options?.enabled !== false &&
    role === "patient" &&
    (options?.age === undefined || options.age >= 18);
  return isSelfMode ? selfQuestions : patientQuestions;
}
